/**
 * TaskModalTabs - Sistema de abas do modal de tarefa
 * 
 * Layout compacto com ícones e contadores.
 * Segue padrão do DealModalTabs (36px).
 * 
 * @version 1.9.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-07 v1.1.0 - Removida aba "Andamentos" (agora é timeline na aba Resumo)
 * RELEASE: 2026-08-09 v1.3.0 - Aba "Veículos" com contador (múltiplos veículos por tarefa)
 * RELEASE: 2026-08-09 v1.4.0 - Aba "Veículos" respeita módulo do tenant + permissão (vehiclesEnabled)
 * RELEASE: 2026-08-21 v1.5.0 - Aba "Chat" (habilitada quando a tarefa tem contato vinculado)
 * RELEASE: 2026-08-22 v1.6.0 - Aba "Financeiro" (cobranças vinculadas à tarefa)
 * RELEASE: 2026-09-13 v1.8.0 - Aba "Fluxos" passa a ser a segunda aba, igual ao
 *          modal de Negócio (vale para Tarefas, Retenção e Jurídico).
 * RELEASE: 2026-09-13 v1.9.0 - Aba "Fluxos" exibe a quantidade de processos
 *          ativos da Tarefa, Retenção ou Jurídico.
 * RELEASE: 2026-09-12 v1.7.0 - Aba "Fluxos" (processos do Workflow ligados à
 *   tarefa/retenção/solicitação jurídica), habilitada apenas com o módulo
 *   Workflow ativo no tenant — mesmo padrão da aba do modal de Negócio
 */
import {
  LayoutDashboard,
  ListChecks,
  Pause,
  Paperclip,
  History,
  Car,
  MessageCircle,
  Receipt,
  GitBranch,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Tipo das abas (v1.7.0: adicionada aba 'workflow')
export type TaskModalTab =
  | 'summary'
  | 'subtasks'
  | 'vehicles'
  | 'chat'
  | 'charges'
  | 'workflow'
  | 'pauses'
  | 'attachments'
  | 'history';

interface TaskModalTabsProps {
  activeTab: TaskModalTab;
  onTabChange: (tab: TaskModalTab) => void;
  subtasksCount?: number;
  pausesCount?: number;
  attachmentsCount?: number;
  /** v1.3.0 — total de veículos vinculados à tarefa */
  vehiclesCount?: number;
  /** v1.4.0 — módulo de veículos habilitado + permissão de visualizar (vehicles_list) */
  vehiclesEnabled?: boolean;
  /** v1.5.0 — total de conversas do contato vinculado à tarefa */
  chatCount?: number;
  /** v1.5.0 — aba Chat só aparece quando há contato/cliente vinculado */
  chatEnabled?: boolean;
  /** v1.6.0 — total de cobranças vinculadas à tarefa */
  chargesCount?: number;
  /** v1.6.0 — aba Financeiro (módulo financeiro + permissão financial_charges) */
  chargesEnabled?: boolean;
  /** v1.7.0 — aba Fluxos (módulo Workflow ativo no tenant) */
  workflowEnabled?: boolean;
  /** v1.9.0 — quantidade de processos ainda ativos vinculados ao registro */
  workflowCount?: number;
}

// Configuração das abas base (v1.3.0: aba Veículos adicionada)
const baseTabsConfig: Array<{
  id: TaskModalTab;
  label: string;
  icon: typeof LayoutDashboard;
  countKey?: 'subtasksCount' | 'pausesCount' | 'attachmentsCount' | 'vehiclesCount' | 'chatCount' | 'chargesCount' | 'workflowCount';
}> = [
  { id: 'summary', label: 'Resumo', icon: LayoutDashboard },
  // v1.8.0: aba Fluxos na segunda posição, igual ao modal de Negócio
  { id: 'workflow', label: 'Fluxos', icon: GitBranch, countKey: 'workflowCount' },
  { id: 'subtasks', label: 'Subtarefas', icon: ListChecks, countKey: 'subtasksCount' },
  { id: 'vehicles', label: 'Veículos', icon: Car, countKey: 'vehiclesCount' },
  { id: 'chat', label: 'Chat', icon: MessageCircle, countKey: 'chatCount' },
  { id: 'charges', label: 'Financeiro', icon: Receipt, countKey: 'chargesCount' },
  { id: 'pauses', label: 'Pausas', icon: Pause, countKey: 'pausesCount' },
  { id: 'attachments', label: 'Anexos', icon: Paperclip, countKey: 'attachmentsCount' },
  { id: 'history', label: 'Histórico', icon: History },
];


/**
 * Sistema de abas do modal (36px)
 */
export function TaskModalTabs({
  activeTab,
  onTabChange,
  subtasksCount,
  pausesCount,
  attachmentsCount,
  vehiclesCount,
  vehiclesEnabled = true,
  chatCount,
  chatEnabled = false,
  chargesCount,
  chargesEnabled = false,
  workflowEnabled = false,
  workflowCount,
}: TaskModalTabsProps) {
  const counts = {
    subtasksCount,
    pausesCount,
    attachmentsCount,
    vehiclesCount,
    chatCount,
    chargesCount,
    workflowCount,
  };

  // v1.4.0/v1.5.0: oculta abas sem módulo/permissão (Veículos) ou sem contato (Chat)
  const visibleTabs = baseTabsConfig.filter((tab) => {
    if (tab.id === 'vehicles' && !vehiclesEnabled) return false;
    if (tab.id === 'chat' && !chatEnabled) return false;
    if (tab.id === 'charges' && !chargesEnabled) return false;
    // v1.7.0: aba Fluxos só aparece com o módulo Workflow ativo no tenant
    if (tab.id === 'workflow' && !workflowEnabled) return false;
    return true;
  });

  return (
    <div className="flex items-center h-9 px-4 border-b bg-muted/20 shrink-0 overflow-x-auto">
      {visibleTabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        const count = tab.countKey ? counts[tab.countKey] : undefined;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'flex items-center gap-1.5 px-3 h-full text-xs font-medium transition-colors relative',
              'hover:text-foreground',
              isActive ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{tab.label}</span>

            {/* Contador */}
            {count !== undefined && count > 0 && (
              <span
                className={cn(
                  'text-[10px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center',
                  isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                )}
              >
                {count}
              </span>
            )}

            {/* Indicador ativo */}
            {isActive && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
            )}
          </button>
        );
      })}
    </div>
  );
}
